import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Switch } from "@shadcn-in-nx/ui/switch";
import { Settings2, RotateCcw, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ALL_RULES, PRESETS, getLiteRules } from "./securityRules";

const SEVERITY_VARIANT = { ERROR: "destructive", WARNING: "warning", INFO: "info" };

const SecurityTemplateConfig = ({ selectedTemplate, onTemplateChange, enabledRules, onRulesChange, isDirty, onSave, maxRules }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const isLite = maxRules && maxRules < ALL_RULES.length;
    const allowedRuleIds = isLite ? getLiteRules(maxRules).map((r) => r.id) : null;

    const handlePreset = (preset) => onTemplateChange(preset);
    const handleToggle = (ruleId, checked) => {
        if (isLite && checked && enabledRules.length >= maxRules) return;
        onRulesChange(checked ? [...enabledRules, ruleId] : enabledRules.filter((id) => id !== ruleId));
    };

    if (!isOpen) {
        return (
            <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
                <Settings2 className="h-4 w-4 mr-1" />
                {t("security.configureRules")}
            </Button>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">{t("security.scanRules")}</CardTitle>
                    <div className="flex gap-2">
                        {isDirty && (
                            <Button size="sm" onClick={onSave}>
                                <Save className="h-4 w-4 mr-1" />
                                {t("security.saveTemplate")}
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                            {t("security.close")}
                        </Button>
                    </div>
                </div>
                {isLite && (
                    <p className="text-xs text-amber-600 mt-1">{maxRules} of {ALL_RULES.length} rules available on your plan</p>
                )}
                <div className="flex gap-2 pt-2">
                    {["basic", "standard", "strict"].map((p) => (
                        <Button key={p} variant={selectedTemplate === p ? "default" : "outline"} size="sm"
                            disabled={isLite && p === "strict"}
                            onClick={() => handlePreset(p)}>
                            {t(`security.template${p.charAt(0).toUpperCase() + p.slice(1)}`)}
                        </Button>
                    ))}
                    {selectedTemplate === "custom" && (
                        <Button variant="outline" size="sm" onClick={() => handlePreset("standard")}>
                            <RotateCcw className="h-3 w-3 mr-1" />
                            {t("security.resetToStandard")}
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ALL_RULES.map((rule) => (
                    <div key={rule.id} className="flex items-center justify-between rounded-md border p-3">
                        <div className="flex-1 min-w-0 mr-3">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{rule.label}</span>
                                <Badge variant={SEVERITY_VARIANT[rule.severity]} className="text-[10px] px-1.5 py-0">
                                    {rule.severity}
                                </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{rule.desc}</p>
                        </div>
                        <Switch
                            checked={enabledRules.includes(rule.id)}
                            onCheckedChange={(checked) => handleToggle(rule.id, checked)}
                            disabled={isLite && !allowedRuleIds.includes(rule.id)}
                        />
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

export { PRESETS };
export default SecurityTemplateConfig;
